import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RoleGuard } from '../../components/auth/RoleGuard';
import { PermissionsSelector } from '../../components/settings/PermissionsSelector';

interface Permission {
  id: string;
  name: string;
  category: string;
}

interface Role {
  id: string;
  name: string;
  permissions: { id: string; name: string }[];
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

const SYSTEM_ROLES = ['platformAdmin', 'tenantAdmin', 'hubManager', 'spokeManager', 'gigWorker', 'admin', 'user'];

export default function RoleDetail() {
  const { id } = useParams<{ id: string }>();
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadRole = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiFetch(`/api/v1/roles/${id}`);
      if (!res.ok) {
        if (res.status === 401) throw new Error('Please log in again');
        if (res.status === 404) throw new Error('Role not found');
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to load role');
        throw new Error(message);
      }
      const data = await res.json();
      setRole(data);
      setEditName(data.name);
      setSelectedPermissions((data.permissions || []).map((p: { id: string }) => p.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load role');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/roles/permissions');
      if (!res.ok) return;
      const data = await res.json();
      setPermissions(Array.isArray(data) ? data : data.items || []);
    } catch {
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    loadRole();
    loadPermissions();
  }, [id, loadRole, loadPermissions]);

  async function saveChanges() {
    if (!id || !role) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch(`/api/v1/roles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName.trim(),
          permissionIds: selectedPermissions,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to update role');
        throw new Error(message);
      }
      const data = await res.json();
      setRole(data);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(false);
    }
  }

  function togglePermission(permissionId: string) {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }

  function isSystemRole(name: string): boolean {
    return SYSTEM_ROLES.includes(name);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <p className="text-destructive mb-4">{error || 'Role not found'}</p>
          <Button variant="outline" asChild>
            <Link to="/settings/roles">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Roles
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const canEditName = !isSystemRole(role.name);
  const canEditPermissions = !isSystemRole(role.name);

  return (
    <div className="min-h-screen">
      <div className="space-y-6">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/settings/roles">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">{role.name}</h1>
          <Badge variant={isSystemRole(role.name) ? 'outline' : 'default'}>
            {isSystemRole(role.name) ? 'System' : 'Custom'}
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Role Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {isEditing && canEditPermissions ? (
              <>
                {canEditName && (
                  <div>
                    <Label htmlFor="roleName">Role Name</Label>
                    <Input
                      id="roleName"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="Enter role name"
                      className="mt-2 max-w-md"
                    />
                  </div>
                )}
                <div>
                  <Label>Permissions</Label>
                  <div className="mt-2">
                    <PermissionsSelector
                      permissions={permissions}
                      selectedIds={selectedPermissions}
                      onToggle={togglePermission}
                    />
                  </div>
                </div>
                {saveError && <p className="text-destructive text-sm">{saveError}</p>}
                <div className="flex gap-2">
                  <Button onClick={saveChanges} disabled={saving || (canEditName && !editName.trim()) || selectedPermissions.length === 0}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-muted-foreground text-sm">Users with this role:</span>
                  <p className="font-medium">{role.userCount ?? 0}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Permissions:</span>
                  <div className="mt-2">
                    {role.permissions.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No permissions assigned</p>
                    ) : permissions.length === 0 ? (
                      <p className="text-muted-foreground text-sm">Loading...</p>
                    ) : (
                      <PermissionsSelector
                        permissions={permissions.filter(p =>
                          role.permissions.some(rp => rp.id === p.id)
                        )}
                        selectedIds={role.permissions.map(p => p.id)}
                        onToggle={() => {}}
                        disabled
                        defaultExpanded
                      />
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Created:</span>
                  <p className="font-medium">{new Date(role.createdAt).toLocaleDateString()}</p>
                </div>
                {canEditPermissions && (
                  <RoleGuard permission="roles.write">
                    <Button onClick={() => setIsEditing(true)}>Edit Role</Button>
                  </RoleGuard>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
